import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../../i18n/i18n';
import './InfoTip.scss';

/** Gap between the trigger and the tip, and the tip's minimum distance from the viewport edge. */
const GAP = 8;
const EDGE = 12;

/**
 * A "?" that explains something on hover or keyboard focus — the app's own
 * tooltip look (the sidebar's `.rail-tip`: raised surface, soft shadow), not
 * the browser's grey `title` box.
 *
 * Rendered into `document.body` and positioned `fixed` from the trigger's
 * rect, like the rail tip: a card's `overflow` can't clip it. Sits above the
 * trigger, or below it when there isn't room above, and is kept inside the
 * viewport horizontally.
 *
 *   <InfoTip label="How inactivity works" title="Inactivity rule">
 *     Place at least one trade every 30 days.
 *   </InfoTip>
 *
 * `label` names the trigger for screen readers; `title` is the tip's heading;
 * `children` its body — text, or rows built with `InfoTip.Row`.
 * `className` styles the trigger, so a page can keep its own "?" look;
 * `glyph` is what the trigger shows ("?" by default, "i" for a definition).
 */
export default function InfoTip({ label, title, children, className = '', glyph = '?' }) {
  const id = useId();
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const show = useCallback(() => setOpen(true), []);
  const hide = useCallback(() => {
    setOpen(false);
    setPos(null);
  }, []);

  // Placed after it renders, once its own size is known.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tipRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const tip = tipRef.current.getBoundingClientRect();
    const above = t.top - GAP - tip.height >= EDGE;
    const left = Math.min(
      Math.max(t.left + t.width / 2 - tip.width / 2, EDGE),
      window.innerWidth - tip.width - EDGE
    );
    setPos({
      top: above ? t.top - GAP - tip.height : t.bottom + GAP,
      left,
      placement: above ? 'top' : 'bottom',
      // Where the arrow points, inside the tip — at the trigger's centre.
      arrow: t.left + t.width / 2 - left,
    });
  }, [open]);

  // Scrolling or resizing moves the trigger out from under a fixed tip.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && hide();
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, hide]);

  return (
    <>
      <span
        ref={triggerRef}
        className={`info-tip__trigger ${className}`.trim()}
        tabIndex={0}
        role="button"
        aria-label={t(label)}
        aria-describedby={open ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {glyph}
      </span>

      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className={`info-tip info-tip--${pos?.placement ?? 'top'}`}
            style={{
              top: pos ? `${pos.top}px` : '-9999px',
              left: pos ? `${pos.left}px` : '-9999px',
              '--info-tip-arrow': pos ? `${pos.arrow}px` : '50%',
              // Measured first, shown once placed — no flash at the wrong spot.
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {title && <span className="info-tip__title">{t(title)}</span>}
            <div className="info-tip__body">{t(children)}</div>
          </div>,
          document.body
        )}
    </>
  );
}

/** A label/value line inside a tip — the value right-aligned, as in the cards. */
InfoTip.Row = function InfoTipRow({ label, children }) {
  return (
    <div className="info-tip__row">
      <span className="info-tip__row-label">{t(label)}</span>
      <span className="info-tip__row-value">{children}</span>
    </div>
  );
};
